import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from '../UI/ConfirmModal';
import {
  User,
  LogOut,
  Menu,
  X,
  Activity,
  Sun,
  Moon,
  LayoutDashboard,
  Settings as SettingsIcon,
  ChevronDown
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setIsMobileMenuOpen(false);
  };

  const handleLogoutConfirm = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Settings', path: '/settings', icon: SettingsIcon },
  ];

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || !isHome
        ? 'glass-header py-3 shadow-premium'
        : 'bg-transparent py-5'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group"
          >
            <div className="p-2 bg-brand-600 rounded-xl text-white shadow-glow-brand transition-transform group-hover:scale-110">
              <Activity className="h-6 w-6" />
            </div>
            <span className={`text-xl font-bold tracking-tight transition-colors ${!scrolled && isHome && theme !== 'dark' ? 'text-surface-900' : 'text-surface-900 dark:text-white'
              }`}>
              Monitor<span className="text-brand-600">Bot</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center space-x-2 text-sm font-semibold text-surface-600 hover:text-brand-600 dark:text-surface-400 dark:hover:text-white transition-all px-3 py-2 rounded-xl hover:bg-brand-500/5"
                  >
                    <link.icon className="h-4 w-4" />
                    <span>{link.name}</span>
                  </Link>
                ))}

                <div className="h-6 w-px bg-surface-200 dark:bg-surface-800 mx-2"></div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={toggleTheme}
                    className="p-2.5 text-surface-500 hover:text-brand-600 dark:text-surface-400 dark:hover:text-white transition-all rounded-xl bg-surface-100 dark:bg-surface-900/50 border border-transparent hover:border-brand-500/20"
                  >
                    {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  </button>

                  <div className="flex items-center space-x-3 pl-2">
                    <div className="flex flex-col text-right">
                      <span className="text-xs font-bold text-surface-900 dark:text-white uppercase tracking-wider">{user?.username}</span>
                      <span className="text-[10px] text-surface-500 font-medium">Pro Plan</span>
                    </div>
                    <button
                      onClick={handleLogoutClick}
                      className="p-2.5 text-danger hover:bg-danger/5 transition-all rounded-xl border border-transparent hover:border-danger/20"
                      title="Logout"
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-5">
                <button
                  onClick={toggleTheme}
                  className="p-2.5 text-surface-500 hover:text-brand-600 dark:text-surface-400 dark:hover:text-white transition-all rounded-xl"
                >
                  {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>

                <Link
                  to="/login"
                  className="text-sm font-bold text-surface-600 hover:text-surface-900 dark:text-surface-400 dark:hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-primary !py-2.5 !px-6"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className="p-2 text-surface-500 hover:text-brand-600 dark:text-surface-400 dark:hover:text-white transition-all rounded-xl"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-xl text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${isMobileMenuOpen ? 'max-h-96 mt-4 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="py-6 space-y-4 border-t border-surface-200 dark:border-surface-800">
            {isAuthenticated ? (
              <div className="space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center space-x-3 text-surface-700 dark:text-surface-300 font-semibold p-3 rounded-2xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <link.icon className="h-5 w-5 text-brand-500" />
                    <span>{link.name}</span>
                  </Link>
                ))}

                <button
                  onClick={handleLogoutClick}
                  className="flex items-center space-x-3 text-danger font-semibold p-3 rounded-2xl hover:bg-danger/5 transition-colors w-full"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                <Link
                  to="/login"
                  className="w-full py-4 text-center font-bold text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-primary w-full py-4 text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Create Account
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access your dashboard and monitors."
        confirmText="Logout"
        cancelText="Stay Logged In"
        variant="warning"
      />
    </nav>
  );
};

export default Navbar;
